import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Cookie from "js-cookie";
import { Grid, Typography, Paper, Box, CircularProgress } from "@mui/material";
import { usePDF } from "react-to-pdf";
import * as XLSX from "xlsx";
import { StyledButton, StyledCancelButton } from "components/StyledComponents/styledComponents";
import { format, differenceInMonths, differenceInDays } from "date-fns";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";
import TdsPayableBifurcatedDialog from "./SalaryPayable";
import GstDialogPopup from "./gstDialogPopup";

const BalanceSheet = ({ startDate, endDate, selectedDsaId, onNavigateToTab }) => {
  const formatINR = (value) =>
    Number(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const { toPDF, targetRef } = usePDF({
    filename: "Balance_Sheet_Report.pdf",
    options: {
      method: "save",
      resolution: 3,
      page: { margin: 10, format: "A4", orientation: "portrait" },
      canvas: { mimeType: "image/png", qualityRatio: 1 },
    },
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Salary Payable modal state ──
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRows, setModalRows] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState(null);

  // ── GST Input Tax Credit modal state ──
  const [gstReceivableOpen, setGstReceivableOpen] = useState(false);
  const [gstReceivableData, setGstReceivableData] = useState([]);
  const [gstReceivableLoading, setGstReceivableLoading] = useState(false);

  const [controller] = useMaterialUIController();
  const { user } = controller;
  const jwt_token = Cookie.get("jwt_token");

  // ── 1. Fetch balance sheet rows ──
  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${URLs.balanceSheet}?startDate=${startDate}&endDate=${endDate}&companyId=${selectedDsaId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${jwt_token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setRows(data);
    } catch (err) {
      console.error("Error fetching balance sheet", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [startDate, endDate, selectedDsaId]);

  // ── 2. Salary Payable fetch (must be defined BEFORE handleLedgerClick) ──
  const handleSalaryPayableClick = async () => {
    setModalOpen(true);
    setModalLoading(true);
    setModalError(null);
    try {
      const res = await fetch(
        `${URLs.salaryPayableBifurcated}?startDate=${startDate}&endDate=${endDate}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${jwt_token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      setModalRows(data);
    } catch (err) {
      console.error("Error fetching TDS Payable bifurcated data", err);
      setModalError(err.message || "Failed to fetch data");
      setModalRows([]);
    } finally {
      setModalLoading(false);
    }
  };

  // ── 3. GST Input Tax Credit fetch (must be defined BEFORE handleAssetLedgerClick) ──
  const handleGstInputTaxCreditClick = async () => {
    setGstReceivableOpen(true);
    setGstReceivableLoading(true);
    setGstReceivableData([]);
    try {
      const res = await fetch(`${URLs.gstreceivable}?startDate=${startDate}&endDate=${endDate}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${jwt_token}`,
          "Content-Type": "application/json",
        },
      });
      if (res.status === 401)
        throw new Error("Unauthorized (401) — your session may have expired.");
      if (!res.ok) throw new Error(`Failed to fetch GST receivable data (${res.status})`);
      setGstReceivableData(await res.json());
    } catch (err) {
      console.error("Error fetching GST receivable data", err);
      setGstReceivableData([]);
      setGstReceivableOpen(false);
    } finally {
      setGstReceivableLoading(false);
    }
  };

  // ── 4. Helpers & click dispatchers (defined AFTER the fetch functions they call) ──
  const isClickableLedger = (ledgerName) =>
    ledgerName === "Salary Payable A/c" || ledgerName === "Duties and Taxes";

  const isClickableAssetLedger = (ledgerName) => ledgerName === "Duties & Taxes";

  const handleLedgerClick = (ledgerName) => {
    if (ledgerName === "Salary Payable A/c") {
      handleSalaryPayableClick(); // ✅ defined above
    } else if (ledgerName === "Duties and Taxes") {
      onNavigateToTab?.(4); // 4 = GST Reports tab
    }
  };

  const handleAssetLedgerClick = (ledgerName) => {
    if (ledgerName === "Duties & Taxes") {
      handleGstInputTaxCreditClick();
    }
  };

  // ── 5. Excel export ──
  const handleExcelExport = () => {
    const liabilitySheet = rows.map((row, idx) => [
      row.liabilityLedger ? idx + 1 : "",
      row.liabilityLedger || "",
      row.liabilityBalance != null && row.liabilityBalance !== 0
        ? parseFloat(Math.abs(row.liabilityBalance).toFixed(2))
        : "",
    ]);
    const assetSheet = rows.map((row, idx) => [
      row.assetLedger ? idx + 1 : "",
      row.assetLedger || "",
      row.assetBalance != null && row.assetBalance !== 0
        ? parseFloat(Math.abs(row.assetBalance).toFixed(2))
        : "",
    ]);

    const maxRows = Math.max(liabilitySheet.length, assetSheet.length);
    while (liabilitySheet.length < maxRows) liabilitySheet.push(["", "", ""]);
    while (assetSheet.length < maxRows) assetSheet.push(["", "", ""]);

    const finalData = [
      ["S.No", "Liability Ledger", "Liability Balance", "S.No", "Asset Ledger", "Asset Balance"],
    ];
    for (let i = 0; i < maxRows; i++) {
      finalData.push([...liabilitySheet[i], ...assetSheet[i]]);
    }

    const totalLiability = rows.reduce(
      (sum, row) =>
        sum +
        (row.liabilityBalance != null && row.liabilityBalance !== 0
          ? Math.abs(row.liabilityBalance)
          : 0),
      0
    );
    const totalAsset = rows.reduce(
      (sum, row) =>
        sum + (row.assetBalance != null && row.assetBalance !== 0 ? Math.abs(row.assetBalance) : 0),
      0
    );

    finalData.push([
      "",
      "Total Liabilities",
      parseFloat(totalLiability.toFixed(2)),
      "",
      "Total Assets",
      parseFloat(totalAsset.toFixed(2)),
    ]);

    const ws = XLSX.utils.aoa_to_sheet(finalData);
    ws["!cols"] = [{ wch: 8 }, { wch: 35 }, { wch: 20 }, { wch: 8 }, { wch: 35 }, { wch: 20 }];

    const numericCols = [2, 5];
    for (let r = 1; r < finalData.length; r++) {
      numericCols.forEach((col) => {
        const cellAddress = XLSX.utils.encode_cell({ r, c: col });
        if (ws[cellAddress] && typeof ws[cellAddress].v === "number") {
          ws[cellAddress].z = "#,##0.00";
        }
      });
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BalanceSheet");
    XLSX.writeFile(wb, "Balance_Sheet_Report.xlsx");
  };

  return (
    <Paper sx={{ p: 3 }} ref={targetRef} elevation={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
        <Typography variant="h6" gutterBottom>
          Balance Sheet
          <br />
          <span
            style={{
              backgroundColor: "#F5F5F5",
              color: "black",
              fontWeight: 600,
              borderRadius: "10px",
              padding: "4px 12px",
              display: "inline-block",
              cursor: "pointer",
              border: "1px solid #ccc",
              transition: "background-color 0.3s ease",
            }}
          >
            From {format(new Date(startDate), "dd-MMM-yyyy")} &nbsp;to&nbsp;
            {format(new Date(endDate), "dd-MMM-yyyy")} &nbsp; (
            {(() => {
              const months = differenceInMonths(new Date(endDate), new Date(startDate));
              const days = differenceInDays(new Date(endDate), new Date(startDate)) % 30;
              if (months > 0 && days > 0)
                return `${months} month${months > 1 ? "s" : ""} ${days} day${days > 1 ? "s" : ""}`;
              else if (months > 0) return `${months} month${months > 1 ? "s" : ""}`;
              else return `${days} day${days > 1 ? "s" : ""}`;
            })()}
            )
          </span>
        </Typography>

        <Box sx={{ textAlign: "center", mb: 2 }}>
          <StyledButton variant="contained" sx={{ mr: 1 }} onClick={() => toPDF()}>
            Download PDF
          </StyledButton>
          <StyledCancelButton variant="outlined" onClick={handleExcelExport}>
            Download Excel
          </StyledCancelButton>
        </Box>
      </Box>

      {/* Table Header */}
      <Grid container sx={{ border: "1px solid #ccc" }}>
        <Grid container>
          <Grid
            item
            xs={6}
            sx={{
              borderRight: "1px solid #ccc",
              p: 1,
              background: "#e0f7fa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              Liabilities
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              p: 1,
              background: "#e0f7fa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
              Assets
            </Typography>
          </Grid>
        </Grid>

        {/* Column Headers */}
        <Grid
          item
          xs={1}
          sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5", textAlign: "center" }}
        >
          <Typography variant="subtitle2">
            <strong>S.No</strong>
          </Typography>
        </Grid>
        <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5" }}>
          <Typography variant="subtitle2">
            <strong>Ledger</strong>
          </Typography>
        </Grid>
        <Grid
          item
          xs={2}
          sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5", textAlign: "right" }}
        >
          <Typography variant="subtitle2">
            <strong>Balance</strong>
          </Typography>
        </Grid>
        <Grid
          item
          xs={1}
          sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5", textAlign: "center" }}
        >
          <Typography variant="subtitle2">
            <strong>S.No</strong>
          </Typography>
        </Grid>
        <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1, background: "#f5f5f5" }}>
          <Typography variant="subtitle2">
            <strong>Ledger</strong>
          </Typography>
        </Grid>
        <Grid item xs={2} sx={{ p: 1, background: "#f5f5f5", textAlign: "right" }}>
          <Typography variant="subtitle2">
            <strong>Balance</strong>
          </Typography>
        </Grid>
      </Grid>

      {/* Table Body */}
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="300px">
          <CircularProgress />
        </Box>
      ) : (
        (() => {
          const maxLength = rows.length;
          return Array.from({ length: maxLength }).map((_, idx) => (
            <Grid
              container
              key={idx}
              sx={{ borderLeft: "1px solid #ccc", borderRight: "1px solid #ccc" }}
            >
              {/* Liabilities S.No */}
              <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                <Typography sx={{ fontSize: "14px" }}>
                  {rows[idx].liabilityLedger ? idx + 1 : ""}
                </Typography>
              </Grid>

              {/* Liabilities Ledger — clickable for Salary Payable A/c and Duties and Taxes */}
              <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    cursor: isClickableLedger(rows[idx].liabilityLedger) ? "pointer" : "default",
                    color: isClickableLedger(rows[idx].liabilityLedger) ? "#1976d2" : "inherit",
                    textDecoration: isClickableLedger(rows[idx].liabilityLedger)
                      ? "underline"
                      : "none",
                  }}
                  onClick={() => {
                    if (rows[idx].liabilityLedger) handleLedgerClick(rows[idx].liabilityLedger);
                  }}
                >
                  {rows[idx].liabilityLedger || ""}
                </Typography>
              </Grid>

              {/* Liabilities Balance */}
              <Grid item xs={2} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "right" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                  {rows[idx]?.liabilityBalance != null && rows[idx]?.liabilityBalance !== 0
                    ? formatINR(Math.abs(rows[idx].liabilityBalance))
                    : ""}
                </Typography>
              </Grid>

              {/* Assets S.No */}
              <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1, textAlign: "center" }}>
                <Typography sx={{ fontSize: "14px" }}>
                  {rows[idx].assetLedger ? idx + 1 : ""}
                </Typography>
              </Grid>

              {/* Assets Ledger — clickable for GST Input Tax Credit */}
              <Grid item xs={3} sx={{ borderRight: "1px solid #ccc", p: 1 }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    cursor: isClickableAssetLedger(rows[idx].assetLedger) ? "pointer" : "default",
                    color: isClickableAssetLedger(rows[idx].assetLedger) ? "#1976d2" : "inherit",
                    textDecoration: isClickableAssetLedger(rows[idx].assetLedger)
                      ? "underline"
                      : "none",
                  }}
                  onClick={() => {
                    if (rows[idx].assetLedger) handleAssetLedgerClick(rows[idx].assetLedger);
                  }}
                >
                  {rows[idx].assetLedger || ""}
                </Typography>
              </Grid>

              {/* Assets Balance */}
              <Grid item xs={2} sx={{ p: 1, textAlign: "right" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                  {rows[idx]?.assetBalance != null && rows[idx]?.assetBalance !== 0
                    ? formatINR(Math.abs(rows[idx].assetBalance))
                    : ""}
                </Typography>
              </Grid>
            </Grid>
          ));
        })()
      )}

      {/* Footer */}
      <Grid container sx={{ border: "1px solid #ccc", borderTop: "none", background: "#f5f5f5" }}>
        <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
        <Grid
          item
          xs={3}
          sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "1rem", fontWeight: "bold" }}
        />
        <Grid
          item
          xs={2}
          sx={{
            borderRight: "1px solid #ccc",
            p: 1,
            fontSize: "1rem",
            textAlign: "right",
            fontWeight: "bold",
          }}
        />
        <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
        <Grid
          item
          xs={3}
          sx={{ fontSize: "1rem", fontWeight: "bold", borderRight: "1px solid #ccc", p: 1 }}
        />
        <Grid item xs={2} sx={{ p: 1, fontSize: "1rem", textAlign: "right", fontWeight: "bold" }} />
      </Grid>

      {/* Salary Payable Dialog */}
      <TdsPayableBifurcatedDialog
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setModalRows([]);
          setModalError(null);
        }}
        startDate={startDate}
        endDate={endDate}
        data={modalRows}
        loading={modalLoading}
        error={modalError}
      />

      {/* GST Input Tax Credit Dialog */}
      <GstDialogPopup
        open={gstReceivableOpen}
        onClose={() => {
          setGstReceivableOpen(false);
          setGstReceivableData([]);
        }}
        loading={gstReceivableLoading}
        data={gstReceivableData}
        mode="gstReceivable"
      />
    </Paper>
  );
};

BalanceSheet.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      liabilityLedger: PropTypes.string,
      liabilityBalance: PropTypes.number,
      assetLedger: PropTypes.string,
      assetBalance: PropTypes.number,
    })
  ),
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  selectedDsaId: PropTypes.number,
  onNavigateToTab: PropTypes.func,
};

export default BalanceSheet;
